import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData } from '../types/shared';
import { ICompany } from './useCompanies';
import {
  IAuditoriaEnvelope,
  ICarteiraAtualizacao,
  ICarteiraKpis,
  ICarteiraDistribuicoes,
  ICarteiraRankings,
  ICarteiraEvolucao,
  IEmpresaDashboard,
  IAuditoriaItem,
  IAuditoriaResumo,
  INotasPage,
  NotaEspecie,
  INotaDrawer,
  INotasResumo,
  NotaTipo,
  IFiscalResumoMes,
  IFiscalMesNota,
  IAcumulador,
  ISocio,
  INotasManuais,
  ICruzamentos,
  IFechamentoEmpresa,
  IImpostosApurados,
  IImpostosPorNota,
  IImpNotasPage,
  ISimples,
  IIntegracao,
  IIntegracaoPendente,
  IIntegracaoDivergencia,
  IObrigacaoEmpresa,
  IObrigacaoCarteira,
  IObrigacoesEcac,
  IEscritorioKpis,
  IEscritorioRankingEmpresa,
  IEscritorioRankingColaborador,
  IClientesFornecedores,
  CliForTipo,
  IBalanceteConsolidado,
  IContabil,
  IContabilLancamento,
  IRelatorioComposicao,
  ISemLancamento,
  IPessoal,
  IDossie,
  IDossieAnaliseIA,
} from '../pages/modulesHome/auditoriaInteligente/types';
import {
  IComposicaoLados,
  ILancamentosLigados,
  IPosicaoRazao,
  IComposicaoSaldo,
  IRazaoConta,
} from '../pages/modulesHome/accountingConciliation/balancete/conta/papelTrabalho';
import { IBalanceteVisaoGeral } from '../pages/modulesHome/accountingConciliation/balancete/visaoGeral';
import { IGrupoEmpresas } from '../pages/modulesHome/accountingConciliation/balancete/grupoEmpresas';

const BASE = '/api/v1/auditoria-inteligente';

/** Período global do módulo (data_ini/data_fim já no formato YYYY-MM-DD). */
export interface IPeriodoParams {
  data_ini?: string;
  data_fim?: string;
}

/**
 * Helper que extrai o conteúdo útil do envelope `{ data, meta, message }`.
 * Todas as rotas do módulo seguem esse padrão (ver INTEGRACAO_FRONTEND.md).
 */
const unwrap = async <T>(url: string, params?: object): Promise<T> => {
  const res = await api.get<IAuditoriaEnvelope<T>>(url, { params });
  return res.data.data;
};

export const useAuditoriaInteligente = () => {
  /* ----------------------- Seletor de empresa ----------------------- */

  // Reaproveita a listagem padrão de empresas (id interno + external_id + razão + cnpj).
  const getEmpresas = async (key: QueryFunctionContext): Promise<IData<ICompany[]>> => {
    const filters = (key.queryKey?.[1] ?? {}) as Record<string, unknown>;
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', { params: filters });
    return res.data;
  };

  const getEmpresaById = async (key: QueryFunctionContext): Promise<ICompany | null> => {
    const id = key.queryKey?.[1] as number | undefined;
    if (!id) return null;
    const res = await api.get<IData<ICompany>>(`/api/v1/empresas/${id}/`);
    return res.data.data as unknown as ICompany;
  };

  /**
   * Grupo matriz/filiais da empresa, pela raiz do CNPJ (backend #1462). Aceita
   * a matriz ou qualquer filial e devolve o grupo inteiro, matriz primeiro.
   * `cobertura=true` traz, por membro, o que existe em extract_* — é o que
   * distingue filial vazia de filial não extraída. A rota não recorta por
   * carteira; é a tela que decide o que fazer com isso.
   */
  const getGrupoEmpresas = async (key: QueryFunctionContext): Promise<IGrupoEmpresas | null> => {
    const id = key.queryKey?.[1] as number | undefined;
    if (!id) return null;
    return unwrap<IGrupoEmpresas>(`/api/v1/empresas/${id}/grupo`, { cobertura: true });
  };

  /* --------------------------- Carteira ----------------------------- */

  const getCarteiraKpis = (key: QueryFunctionContext): Promise<ICarteiraKpis> =>
    unwrap<ICarteiraKpis>(`${BASE}/carteira/kpis`, key.queryKey?.[1] as IPeriodoParams);

  const getCarteiraDistribuicoes = (key: QueryFunctionContext): Promise<ICarteiraDistribuicoes> =>
    unwrap<ICarteiraDistribuicoes>(
      `${BASE}/carteira/distribuicoes`,
      key.queryKey?.[1] as IPeriodoParams,
    );

  const getCarteiraRankings = (key: QueryFunctionContext): Promise<ICarteiraRankings> =>
    unwrap<ICarteiraRankings>(`${BASE}/carteira/rankings`, key.queryKey?.[1] as IPeriodoParams);

  const getCarteiraEvolucao = (key: QueryFunctionContext): Promise<ICarteiraEvolucao[]> =>
    unwrap<ICarteiraEvolucao[]>(`${BASE}/carteira/evolucao`, key.queryKey?.[1] as IPeriodoParams);

  // Recomputa TODOS os agregados de carteira da combinação de filtros (kpis,
  // distribuições, rankings, evolução). O servidor aplica cooldown de 5h por
  // combinação e responde 429 (com `detail`) dentro da janela.
  const postCarteiraAtualizar = async (
    params: IPeriodoParams & { somente_ativas?: boolean | 1 },
  ): Promise<ICarteiraAtualizacao> => {
    const res = await api.post<IAuditoriaEnvelope<ICarteiraAtualizacao>>(
      `${BASE}/carteira/atualizar`,
      {},
      { params },
    );
    return res.data.data;
  };

  /* ----------------------- Empresa (individual) --------------------- */

  const getEmpresaDashboard = (key: QueryFunctionContext): Promise<IEmpresaDashboard> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IEmpresaDashboard>(`${BASE}/empresas/${id}/dashboard`, periodo);
  };

  /* --------------------------- Auditorias --------------------------- */

  const getAuditorias = (key: QueryFunctionContext): Promise<IAuditoriaItem[]> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      status?: string;
      area?: string;
      regra?: string;
    };
    return unwrap<IAuditoriaItem[]>(`${BASE}/empresas/${id}/auditorias`, params);
  };

  const getAuditoriasResumo = (key: QueryFunctionContext): Promise<IAuditoriaResumo> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IAuditoriaResumo>(`${BASE}/empresas/${id}/auditorias/resumo`, periodo);
  };

  /* ----------------------------- Notas ------------------------------ */

  const getNotas = (key: QueryFunctionContext): Promise<INotasPage> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      tipo?: NotaTipo;
      especie?: NotaEspecie;
      busca?: string;
      apenas_ativas?: boolean;
      limit?: number;
      offset?: number;
    };
    return unwrap<INotasPage>(`${BASE}/empresas/${id}/notas`, params);
  };

  const getNotasResumo = (key: QueryFunctionContext): Promise<INotasResumo> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      tipo?: NotaTipo;
      apenas_ativas?: boolean;
    };
    return unwrap<INotasResumo>(`${BASE}/empresas/${id}/notas/resumo`, params);
  };

  const getNotaDrawer = (key: QueryFunctionContext): Promise<INotaDrawer> => {
    const { id, tipo, codigo } = (key.queryKey?.[1] ?? {}) as {
      id: number;
      tipo: string;
      codigo: string;
    };
    return unwrap<INotaDrawer>(`${BASE}/empresas/${id}/notas/${tipo}/${codigo}/drawer`);
  };

  /* ----------------------- Fiscal (visão geral) --------------------- */

  const getFiscalResumoMensal = (key: QueryFunctionContext): Promise<IFiscalResumoMes[]> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IFiscalResumoMes[]>(`${BASE}/empresas/${id}/fiscal/resumo-mensal`, periodo);
  };

  const getFiscalMesNotas = (key: QueryFunctionContext): Promise<IFiscalMesNota[]> => {
    const { id, mes } = (key.queryKey?.[1] ?? {}) as { id: number; mes: string };
    return unwrap<IFiscalMesNota[]>(`${BASE}/empresas/${id}/fiscal/mes/${mes}/notas`);
  };

  /* --------------------------- Acumuladores ------------------------- */

  // extract_acumuladores v2 é mensal — a rota agrega os meses do range global.
  const getAcumuladores = (key: QueryFunctionContext): Promise<IAcumulador[]> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IAcumulador[]>(`${BASE}/empresas/${id}/acumuladores`, periodo);
  };

  /* ------------------------ Quadro societário ----------------------- */

  // Cadastral — não recebe range.
  const getSocietario = (key: QueryFunctionContext): Promise<ISocio[]> => {
    const id = key.queryKey?.[1] as number;
    return unwrap<ISocio[]>(`${BASE}/empresas/${id}/societario`);
  };

  /* ---------------------------- Notas manuais ----------------------- */

  const getNotasManuais = (key: QueryFunctionContext): Promise<INotasManuais> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<INotasManuais>(`${BASE}/empresas/${id}/notas-manuais`, periodo);
  };

  /* ------------------------- Cruzamentos por área ------------------- */

  const getCruzamentos = (key: QueryFunctionContext): Promise<ICruzamentos> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<ICruzamentos>(`${BASE}/empresas/${id}/cruzamentos`, periodo);
  };

  /* ------------------- Acompanhamento de fechamento ----------------- */

  // Multi-empresa (escritório) — não recebe empresa nem range.
  const getFechamento = (): Promise<IFechamentoEmpresa[]> =>
    unwrap<IFechamentoEmpresa[]>(`${BASE}/escritorio/fechamento`);

  /* ------------------------- Impostos apurados ---------------------- */

  const getImpostosApurados = (key: QueryFunctionContext): Promise<IImpostosApurados> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IImpostosApurados>(`${BASE}/empresas/${id}/impostos-apurados`, periodo);
  };

  /* ------------------------- Impostos por nota ---------------------- */

  const getImpostosPorNota = (key: QueryFunctionContext): Promise<IImpostosPorNota> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      tipo: NotaTipo;
      modelo?: string;
      imposto?: string;
      cfop?: string;
    };
    return unwrap<IImpostosPorNota>(`${BASE}/empresas/${id}/impostos-por-nota`, params);
  };

  const getImpostosPorNotaNotas = (key: QueryFunctionContext): Promise<IImpNotasPage> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      tipo: NotaTipo;
      cfop?: string;
      limit?: number;
      offset?: number;
    };
    return unwrap<IImpNotasPage>(`${BASE}/empresas/${id}/impostos-por-nota/notas`, params);
  };

  /* --------------------------- Simples Nacional --------------------- */

  const getSimples = (key: QueryFunctionContext): Promise<ISimples> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<ISimples>(`${BASE}/empresas/${id}/simples`, periodo);
  };

  /* ----------------------- Integração fiscal-contábil --------------- */

  const getIntegracao = (key: QueryFunctionContext): Promise<IIntegracao> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IIntegracao>(`${BASE}/empresas/${id}/integracao`, periodo);
  };

  const getIntegracaoPendentes = (key: QueryFunctionContext): Promise<IIntegracaoPendente[]> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      limit?: number;
    };
    return unwrap<IIntegracaoPendente[]>(`${BASE}/empresas/${id}/integracao/pendentes`, params);
  };

  const getIntegracaoDivergencias = (
    key: QueryFunctionContext,
  ): Promise<IIntegracaoDivergencia[]> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      limit?: number;
    };
    return unwrap<IIntegracaoDivergencia[]>(
      `${BASE}/empresas/${id}/integracao/divergencia-valor`,
      params,
    );
  };

  /* --------------------------- Obrigações --------------------------- */

  const getObrigacoesEmpresa = (key: QueryFunctionContext): Promise<IObrigacaoEmpresa[]> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IObrigacaoEmpresa[]>(`${BASE}/empresas/${id}/obrigacoes`, periodo);
  };

  const getObrigacoesCarteira = (key: QueryFunctionContext): Promise<IObrigacaoCarteira[]> =>
    unwrap<IObrigacaoCarteira[]>(
      `${BASE}/carteira/obrigacoes`,
      key.queryKey?.[1] as IPeriodoParams,
    );

  // Registro oficial da Receita (eCAC) por empresa.
  const getObrigacoesEcac = (key: QueryFunctionContext): Promise<IObrigacoesEcac> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IObrigacoesEcac>(`${BASE}/empresas/${id}/obrigacoes/ecac`, periodo);
  };

  /* ----------------------- Gestão do escritório --------------------- */

  const getEscritorioKpis = (key: QueryFunctionContext): Promise<IEscritorioKpis> =>
    unwrap<IEscritorioKpis>(`${BASE}/escritorio/kpis`, key.queryKey?.[1] as IPeriodoParams);

  const getEscritorioRankingEmpresas = (
    key: QueryFunctionContext,
  ): Promise<IEscritorioRankingEmpresa[]> =>
    unwrap<IEscritorioRankingEmpresa[]>(
      `${BASE}/escritorio/ranking-empresas`,
      key.queryKey?.[1] as IPeriodoParams,
    );

  const getEscritorioRankingColaboradores = (
    key: QueryFunctionContext,
  ): Promise<IEscritorioRankingColaborador[]> =>
    unwrap<IEscritorioRankingColaborador[]>(
      `${BASE}/escritorio/ranking-colaboradores`,
      key.queryKey?.[1] as IPeriodoParams,
    );

  /* ----------------------- Clientes & fornecedores ------------------ */

  const getClientesFornecedores = (key: QueryFunctionContext): Promise<IClientesFornecedores> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      tipo: CliForTipo;
    };
    return unwrap<IClientesFornecedores>(`${BASE}/empresas/${id}/clientes-fornecedores`, params);
  };

  /* ------------------------------ Contábil -------------------------- */

  const getContabil = (key: QueryFunctionContext): Promise<IContabil> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IContabil>(`${BASE}/empresas/${id}/contabil`, periodo);
  };

  /**
   * Balancete SOMADO dos estabelecimentos marcados (rota irmã).
   *
   * É SOMA SIMPLES: nada é eliminado, e o payload traz `avisos` que a tela
   * é obrigada a exibir — a folha de uma filial pode estar lançada nos dois
   * livros e as contas intercompany continuam no total. A regra de
   * eliminação depende do contador (ver `balancete_consolidado.py`).
   */
  const getBalanceteConsolidado = (key: QueryFunctionContext): Promise<IBalanceteConsolidado> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      empresas: string;
    };
    return unwrap<IBalanceteConsolidado>(
      `${BASE}/empresas/${id}/contabil/balancete/consolidado`,
      params,
    );
  };

  /**
   * Relatório da Composição de Saldo: carteira, empresa ou grupo.
   *
   * `empresas` ausente significa CARTEIRA INTEIRA — e a rota exige
   * Admin/Desenvolvimento nesse modo, porque é o agregado de 518 empresas.
   * Mandar string vazia seria ambíguo, então `paramEmpresas` devolve
   * `undefined` quando não há recorte.
   */
  const getComposicaoRelatorio = (key: QueryFunctionContext): Promise<IRelatorioComposicao> => {
    const params = (key.queryKey?.[1] ?? {}) as {
      empresas?: string;
      data_posicao?: string;
      limite?: number;
    };
    return unwrap<IRelatorioComposicao>(`${BASE}/contabil/composicao-relatorio`, params);
  };

  /**
   * Empresas ATIVAS com saldo a compor e NENHUM lançamento carregado.
   *
   * Lista de carteira, sem recorte: a ausência dessas empresas no relatório
   * NÃO significa que estão em dia, e é isso que esta chamada existe para
   * dizer. Medido em 02/10: 25 empresas, R$ 374.247.392,53.
   */
  const getComposicaoSemLancamento = (key: QueryFunctionContext): Promise<ISemLancamento> => {
    const params = (key.queryKey?.[1] ?? {}) as { limite?: number };
    return unwrap<ISemLancamento>(`${BASE}/contabil/composicao-relatorio/sem-lancamento`, params);
  };

  const getContabilLancamentos = (key: QueryFunctionContext): Promise<IContabilLancamento[]> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      limit?: number;
      offset?: number;
    };
    return unwrap<IContabilLancamento[]>(`${BASE}/empresas/${id}/contabil/lancamentos`, params);
  };

  /**
   * Visão geral do balancete em TODA a carteira — alimenta os cards de empresa
   * da Composição de Saldo.
   *
   * O endpoint NÃO pagina (aceita só `busca`, `status` e o range de datas):
   * devolve a carteira inteira mais o `resumo` por status. A paginação de 10
   * cards é recorte no cliente, de propósito — ver `EMPRESAS_POR_PAGINA`.
   */
  const getBalanceteVisaoGeral = (key: QueryFunctionContext): Promise<IBalanceteVisaoGeral> =>
    unwrap<IBalanceteVisaoGeral>(
      `${BASE}/contabil/balancete/visao-geral`,
      (key.queryKey?.[1] ?? {}) as IPeriodoParams & { busca?: string; status?: string },
    );

  /**
   * Composição de saldo da conta (Papel de trabalho). NÃO é razão: devolve os
   * itens EM ABERTO que somam o saldo na data-posição + a linha de conciliação.
   * `id` é o id INTERNO da empresa (tb_empresa.id), igual às demais rotas.
   */
  const getComposicaoSaldo = (key: QueryFunctionContext): Promise<IComposicaoSaldo> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      conta: string;
      data_posicao?: string;
    };
    return unwrap<IComposicaoSaldo>(`${BASE}/empresas/${id}/contabil/composicao-saldo`, params);
  };

  /**
   * Indicadores da composição separados em A RECEBER (contas de ativo) e
   * A PAGAR (contas de passivo).
   *
   * O lado vem da CLASSIFICAÇÃO da conta, não do `sinal` do item nem da
   * natureza do saldo — a natureza é do SALDO ATUAL (que inverte) e jogaria
   * fornecedor no 'a receber' em 180 contas da carteira.
   */
  const getComposicaoLados = (key: QueryFunctionContext): Promise<IComposicaoLados> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as {
      id: number;
      data_posicao?: string;
      /**
       * O ano contábil pedido. Vai na chave da query, por isso trocar o filtro
       * de data REFAZ a consulta — antes a chave tinha só a empresa e o ano
       * anterior repetia os números do corrente.
       */
      exercicio?: number;
    };
    return unwrap<IComposicaoLados>(`${BASE}/empresas/${id}/contabil/composicao-lados`, params);
  };

  /**
   * Lançamentos buscados pelo NÚMERO, sem filtro de data.
   *
   * O painel de pagamentos de um título usa isto: `ligacoes` guarda os
   * números, e só com eles dá para mostrar TODOS. Antes o painel dependia da
   * página carregada do razão — medido na NF 22401, 9 de 13 pagamentos
   * sumiam, e nenhum era de outro exercício.
   */
  const getLancamentosPorNumero = (key: QueryFunctionContext): Promise<ILancamentosLigados> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      numeros: string;
      conta?: string;
    };
    return unwrap<ILancamentosLigados>(`${BASE}/empresas/${id}/contabil/lancamentos`, params);
  };

  /** Em que página do razão da conta um lançamento cai. */
  const getPosicaoNoRazao = (key: QueryFunctionContext): Promise<IPosicaoRazao> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      conta: string;
      numero: number;
      por_pagina: number;
    };
    return unwrap<IPosicaoRazao>(`${BASE}/empresas/${id}/contabil/razao/posicao`, params);
  };

  /**
   * Razão da conta: TODO o movimento do período, com saldo acumulado.
   *
   * Responde outra pergunta que a composição. A composição mostra o que ficou
   * EM ABERTO (resultado do matching provisão × baixa); a razão mostra o que
   * ACONTECEU, inclusive o que já se anulou. Medido na 856/177 em jan–jun/2026:
   * 3.325 lançamentos de movimento contra dezenas de itens em aberto — a
   * diferença de tamanho entre as duas abas é esperada, não é divergência.
   */
  const getRazaoConta = (key: QueryFunctionContext): Promise<IRazaoConta> => {
    const { id, ...params } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & {
      id: number;
      conta: string;
    };
    return unwrap<IRazaoConta>(`${BASE}/empresas/${id}/contabil/razao`, params);
  };

  /**
   * Baixa o papel de trabalho da conta em XLSX (rota irmã `/composicao-saldo/xlsx`).
   *
   * O arquivo é gerado NO BACKEND de propósito: o front não tem capacidade real
   * de XLSX (só CSV-com-BOM e jsPDF) e, principalmente, montar a planilha aqui
   * duplicaria as regras da composição fora do motor — arquivo e tela poderiam
   * divergir. Aqui só disparamos o download do binário.
   *
   * O arquivo tem DUAS ABAS, as da tela: "Conta analítica" (a composição, com
   * as colunas VISÍVEIS que o usuário escolheu — `colunas`, títulos separados
   * por vírgula, normalizados por `paramColunas`) e "Razão" (o movimento
   * inteiro do período, sem paginação). O filtro (`escopo=filtrado`) recorta só
   * a composição — a razão não é filtrada na tela nem no arquivo.
   *
   * O nome do arquivo vem do `Content-Disposition` (o backend expõe o header via
   * `Access-Control-Expose-Headers`); o fallback cobre o caso do header não
   * chegar ao XHR (proxy que filtra, por exemplo).
   */
  const baixarComposicaoSaldoXlsx = async (params: {
    id: number;
    conta: string;
    data_ini: string;
    data_fim: string;
    data_posicao?: string;
    /**
     * 'completo' = papel de trabalho da conta inteira (padrão do backend);
     * 'filtrado' = apenas o recorte dos filtros enviados. No filtrado o arquivo
     * declara no cabeçalho que é um recorte e lista os filtros aplicados — sem
     * isso um recorte passaria por papel de trabalho completo.
     */
    escopo?: 'completo' | 'filtrado';
    /** Títulos das colunas visíveis, separados por vírgula (`paramColunas`). */
    colunas?: string;
    [filtro: string]: string | number | undefined;
  }): Promise<void> => {
    const { id, ...query } = params;
    const res = await api.get<Blob>(`${BASE}/empresas/${id}/contabil/composicao-saldo/xlsx`, {
      params: query,
      responseType: 'blob',
    });

    const cd = String(res.headers?.['content-disposition'] ?? '');
    const doHeader = /filename="?([^";]+)"?/i.exec(cd)?.[1];
    const nome = doHeader || `papel-trabalho_conta-${query.conta}_${query.data_fim}.xlsx`;

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nome);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  /* ------------------------------- Pessoal -------------------------- */

  const getPessoal = (key: QueryFunctionContext): Promise<IPessoal> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IPessoal>(`${BASE}/empresas/${id}/pessoal`, periodo);
  };

  /* ------------------------------ Dossiê 360 ------------------------ */

  const getDossie = (key: QueryFunctionContext): Promise<IDossie> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IDossie>(`${BASE}/empresas/${id}/dossie`, periodo);
  };

  // Última análise IA SALVA pra empresa+período (leitura pura, não chama a
  // Claude). Retorna null se nunca foi gerada.
  const getDossieAnaliseIASalva = (key: QueryFunctionContext): Promise<IDossieAnaliseIA | null> => {
    const { id, ...periodo } = (key.queryKey?.[1] ?? {}) as IPeriodoParams & { id: number };
    return unwrap<IDossieAnaliseIA | null>(`${BASE}/empresas/${id}/dossie/analise-ia`, periodo);
  };

  // On-demand (botão) — consome quota da API da Claude. Pode responder 503,
  // ou 429 se a análise da empresa+período foi gerada há menos de 5h.
  const postDossieAnaliseIA = async (
    id: number,
    periodo?: IPeriodoParams,
  ): Promise<IDossieAnaliseIA> => {
    const res = await api.post<IAuditoriaEnvelope<IDossieAnaliseIA>>(
      `${BASE}/empresas/${id}/dossie/analise-ia`,
      {},
      { params: periodo },
    );
    return res.data.data;
  };

  return {
    getEmpresas,
    getEmpresaById,
    getGrupoEmpresas,
    getCarteiraKpis,
    getCarteiraDistribuicoes,
    getCarteiraRankings,
    getCarteiraEvolucao,
    postCarteiraAtualizar,
    getEmpresaDashboard,
    getAuditorias,
    getAuditoriasResumo,
    getNotas,
    getNotasResumo,
    getNotaDrawer,
    getFiscalResumoMensal,
    getFiscalMesNotas,
    getAcumuladores,
    getSocietario,
    getNotasManuais,
    getCruzamentos,
    getFechamento,
    getImpostosApurados,
    getImpostosPorNota,
    getImpostosPorNotaNotas,
    getSimples,
    getIntegracao,
    getIntegracaoPendentes,
    getIntegracaoDivergencias,
    getObrigacoesEmpresa,
    getObrigacoesCarteira,
    getObrigacoesEcac,
    getEscritorioKpis,
    getEscritorioRankingEmpresas,
    getEscritorioRankingColaboradores,
    getClientesFornecedores,
    getContabil,
    getComposicaoRelatorio,
    getComposicaoSemLancamento,
    getBalanceteConsolidado,
    getContabilLancamentos,
    getBalanceteVisaoGeral,
    getComposicaoSaldo,
    getComposicaoLados,
    getLancamentosPorNumero,
    getPosicaoNoRazao,
    getRazaoConta,
    baixarComposicaoSaldoXlsx,
    getPessoal,
    getDossie,
    getDossieAnaliseIASalva,
    postDossieAnaliseIA,
  };
};
